import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { BrandLogo } from '@/components/system/BrandLogo';
import { cn } from '@/lib/cn';
import {
  AlertTriangle,
  BarChart3,
  Bot,
  Building2,
  Contact2,
  CreditCard,
  DollarSign,
  FileBarChart,
  GitBranch,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Palette,
  Puzzle,
  Settings,
  Shield,
  Sparkles,
  Target,
  Users,
  Users2,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import type { ComponentType } from 'react';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

interface NavGroup {
  label: string;
  adminOnly?: boolean;
  items: NavItem[];
}

// Grouped around agency workflow (Dashboard, Clients, Campaigns, Automations,
// CRM, Reports, Creative, AI Assistant, Billing, Settings, Support) rather
// than the previous generic Platform/Workspace/Intelligence taxonomy. Every
// existing page stays reachable — nested under whichever group it's most
// relevant to instead of being removed.
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Dashboard',
    items: [{ to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Clients',
    items: [
      { to: '/app/clients', label: 'Clients', icon: Building2 },
      { to: '/app/migration', label: 'Migration Center', icon: Users2 },
    ],
  },
  {
    label: 'Campaigns',
    items: [
      { to: '/app/campaigns', label: 'Campaigns', icon: Target },
      { to: '/app/audiences', label: 'Audiences', icon: Users2 },
    ],
  },
  {
    label: 'Automations',
    items: [
      { to: '/app/automation', label: 'Automations', icon: Bot },
      { to: '/app/journeys', label: 'Journeys', icon: GitBranch },
      { to: '/app/personalization', label: 'Personalization', icon: Sparkles },
      { to: '/app/moderation', label: 'Moderation', icon: Shield },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/app/crm', label: 'CRM', icon: Contact2 },
      { to: '/app/email-marketing', label: 'Email', icon: Mail },
    ],
  },
  {
    label: 'Reports',
    items: [
      { to: '/app/analytics', label: 'Reports', icon: FileBarChart },
      { to: '/app/ad-spend', label: 'Ad Spend', icon: DollarSign },
      { to: '/app/tracking', label: 'Tracking', icon: BarChart3 },
    ],
  },
  {
    label: 'Creative',
    items: [{ to: '/app/creative', label: 'Creative', icon: ImageIcon }],
  },
  {
    label: 'AI Assistant',
    items: [{ to: '/app/ai-assistant', label: 'AI Assistant', icon: Sparkles }],
  },
  {
    label: 'Billing',
    items: [{ to: '/app/billing', label: 'Billing', icon: CreditCard }],
  },
  {
    label: 'Admin',
    adminOnly: true,
    items: [
      { to: '/app/admin', label: 'Admin Dashboard', icon: LayoutDashboard },
      { to: '/app/admin/error-monitoring', label: 'Error Monitoring', icon: AlertTriangle },
      { to: '/app/admin/security-monitoring', label: 'Security', icon: Shield },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/app/settings', label: 'Settings', icon: Settings },
      { to: '/app/team', label: 'Team', icon: Users },
      { to: '/app/organization', label: 'Organization', icon: Building2 },
      { to: '/app/integrations', label: 'Integrations', icon: Puzzle },
      { to: '/app/api-keys', label: 'API Keys', icon: KeyRound },
      { to: '/app/settings/white-label', label: 'White Label', icon: Palette },
    ],
  },
  {
    label: 'Support',
    items: [{ to: '/app/support', label: 'Support', icon: LifeBuoy }],
  },
];

export function AppSidebar({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const location = useLocation();
  const { isPlatformAdmin } = usePlatformAdmin();

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => { document.body.classList.remove('sidebar-open'); };
  }, [mobileOpen]);

  const visibleGroups = NAV_GROUPS.filter(group => !group.adminOnly || isPlatformAdmin);

  const sidebarContent = (
    <div className="flex w-full flex-col overflow-hidden rounded-2xl backdrop-blur-2xl"
      style={{
        background: 'var(--ui-sidebar)',
        border: '1px solid var(--ui-sidebar-border)',
        boxShadow: '0 0 0 1px rgba(99,102,241,0.05)',
      }}>
        {/* Logo */}
        <div className="flex-none px-5 py-5" style={{ borderBottom: '1px solid var(--ui-border)' }}>
          <BrandLogo />
          <p className="mt-1 font-mono text-[9px] font-semibold uppercase tracking-[0.6em] text-white/20">
            Command Center
          </p>
        </div>

        {/* Navigation — scrollable */}
        <nav
          className="flex-1 overflow-y-auto px-3 py-4"
          style={{ scrollbarWidth: 'none' }}
        >
          {visibleGroups.map((group) => (
            <div key={group.label} className="mb-5 last:mb-2">
              <p className="mb-1.5 px-3 font-mono text-[8px] font-bold uppercase tracking-[0.6em] text-white/15">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const isExternal = item.to.includes(':');
                  const isActive =
                    !isExternal &&
                    (location.pathname === item.to || location.pathname.startsWith(item.to + '/'));

                  const linkClassName = cn(
                    'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-accent-500/10 text-white shadow-[inset_0_0_0_1px_rgba(99,102,241,0.15)]'
                      : 'text-white/35 hover:bg-white/[0.04] hover:text-white/70'
                  );
                  const iconClassName = cn(
                    'h-[15px] w-[15px] flex-none transition-colors duration-150',
                    isActive ? 'text-accent-300' : 'text-white/30 group-hover:text-white/60'
                  );

                  if (isExternal) {
                    return (
                      <a key={item.to} href={item.to} onClick={onMobileClose} className={linkClassName}>
                        <item.icon className={iconClassName} />
                        <span className="flex-1 truncate text-[13px]">{item.label}</span>
                      </a>
                    );
                  }

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={onMobileClose}
                      className={linkClassName}
                    >
                      {/* Left accent bar */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-accent-400" />
                      )}
                      <item.icon className={iconClassName} />
                      <span className="flex-1 truncate text-[13px]">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom: system status */}
        <div className="flex-none px-4 py-4" style={{ borderTop: '1px solid var(--ui-border)' }}>
          <div className="flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5 flex-none">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            <span className="font-mono text-[9px] text-white/25">All systems operational</span>
          </div>
        </div>
      </div>
  );

  return (
    <>
      {/* Desktop sidebar — always visible on large screens */}
      <aside
        className="sticky top-5 hidden w-56 flex-none lg:flex"
        style={{ height: 'calc(100vh - 2.5rem)' }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar — animated overlay drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onMobileClose}
            />
            {/* Drawer */}
            <motion.aside
              key="drawer"
              className="fixed inset-y-0 left-0 z-50 flex w-[72vw] max-w-[288px] flex-col lg:hidden"
              style={{ padding: '1rem', paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))', paddingLeft: 'calc(1rem + env(safe-area-inset-left, 0px))' }}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280, mass: 0.8 }}
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
